import { Dashboard } from "./Dashboard.js";
import { FilterPanel } from "./FilterPanel.js";
import { Charts } from "./Charts.js";
import { CONFIG } from "../config.js";
import {
  formatCurrency,
  formatDateShort,
  gregorianToBuddhist,
} from "../utils/formatters.js";
import {
  calculateSummary,
  groupByCategory,
  groupByProject,
  sortByDate,
} from "../services/dataProcessor.js";

/**
 * Reports Page Component
 * Premium Reporting with Liquid Glass style + Professional Print
 */
export class ReportsPage {
  constructor() {
    this.dashboard = new Dashboard();
    this.filterPanel = new FilterPanel();
    this.charts = new Charts();
    this.metadata = null;
    this.currentFilters = {};
  }

  /**
   * Render reports page
   */
  render() {
    return `
      <div class="page-container print-container">
        <div class="page-header reports-page-header">
          <div>
            <h1>📊 ລາຍງານການເງິນ</h1>
            <p class="page-subtitle">ວິເຄາະຂໍ້ມູນລາຍຮັບ-ລາຍຈ່າຍຢ່າງລະອຽດດ້ວຍລະບົບອັດສະລິຍະ</p>
          </div>
          <button id="printReportBtn" class="btn btn-primary no-print" style="gap: 0.5rem;">
            🖨️ ພິມລາຍງານ (PDF)
          </button>
        </div>

        <!-- Filter Panel -->
        <div id="filterPanelContainer"></div>

        <!-- Dashboard Summary -->
        <div id="dashboardContainer"></div>

        <!-- Charts Section -->
        <div class="charts-section">
          <h2 class="section-title">
            📈 ກາຟວິເຄາະຂໍ້ມູນ
          </h2>
          <div id="chartsContainer"></div>
        </div>

        <!-- Transaction List -->
        <div class="transactions-section">
          <div id="transactionListContainer"></div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div id="deleteModalContainer"></div>

        <div style="height: 60px;"></div>
      </div>
    `;
  }

  /**
   * Initialize reports page after rendering
   */
  async initialize() {
    this.showSectionLoaders();

    // Print Button Logic
    const printBtn = document.getElementById("printReportBtn");
    if (printBtn) {
      printBtn.addEventListener("click", () => {
        this.generatePrintReport();
      });
    }

    try {
      const { googleSheetsService } =
        await import("../services/googleSheets.js");
      this.metadata = await googleSheetsService.getMetadata();
    } catch (error) {
      console.error("Error loading metadata:", error);
    }

    // Render filter panel
    const filterContainer = document.getElementById("filterPanelContainer");
    if (filterContainer) {
      filterContainer.innerHTML = this.filterPanel.render(this.metadata);
      this.filterPanel.initialize();
      this.filterPanel.setFilterCallback((filters) =>
        this.handleFilterChange(filters),
      );
    }

    // Render delete modal FIRST so handlers can be attached
    const deleteModalContainer = document.getElementById(
      "deleteModalContainer",
    );
    if (deleteModalContainer) {
      deleteModalContainer.innerHTML = this.dashboard.renderDeleteModal();
      this.dashboard.attachModalHandlers();
    }

    await this.loadData();

    // Listen for transaction deleted event to refresh data
    window.addEventListener("transactionDeleted", () => {
      this.loadData();
    });
  }

  /**
   * Show elegant loaders
   */
  showSectionLoaders() {
    const containers = [
      "dashboardContainer",
      "chartsContainer",
      "transactionListContainer",
    ];
    containers.forEach((id) => {
      const el = document.getElementById(id);
      if (el) {
        el.innerHTML = `
          <div class="glass-panel" style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 300px; border-style: dashed;">
             <div class="liquid-spinner"></div>
             <p style="margin-top: 1.5rem; font-weight: 700; color: var(--primary-green); font-size: 1.1rem;">ກຳລັງໂຫຼດຂໍ້ມູນ...</p>
          </div>
        `;
      }
    });
  }

  /**
   * Handle filter change — show loaders, fetch, then render
   */
  async handleFilterChange(filters) {
    this.currentFilters = filters;
    await this.loadData();
  }

  /**
   * Load data with current filters
   */
  async loadData() {
    // Show loading state on filter panel controls
    this.filterPanel.setLoading(true);
    // Show section loaders while fetching
    this.showSectionLoaders();

    try {
      await this.dashboard.loadData(this.currentFilters);

      // Render summary and charts in parallel (they don't depend on each other)
      const dashboardContainer = document.getElementById("dashboardContainer");
      const chartsContainer = document.getElementById("chartsContainer");

      if (dashboardContainer) {
        dashboardContainer.innerHTML = this.dashboard.renderSummary();
      }

      if (chartsContainer) {
        chartsContainer.innerHTML = this.charts.render();
        this.charts.initialize(this.dashboard.data);
      }

      // Render transaction list
      const transactionContainer = document.getElementById(
        "transactionListContainer",
      );
      if (transactionContainer) {
        transactionContainer.innerHTML = this.dashboard.renderTransactionList();
      }

      // Add event listener for Load More button after rendering
      this.attachLoadMoreHandler();

      // Attach delete button handlers (for the list only)
      this.dashboard.attachTransactionListHandlers();
    } catch (error) {
      console.error("Error loading reports data:", error);
      const dashboardContainer = document.getElementById("dashboardContainer");
      if (dashboardContainer) {
        dashboardContainer.innerHTML = `<div class="glass-panel error-page">⚠️ ບໍ່ສາມາດໂຫຼດຂໍ້ມູນໄດ້: ${error.message}</div>`;
      }
    } finally {
      // Always restore filter panel controls
      this.filterPanel.setLoading(false);
    }
  }

  /**
   * Attach event handler for Load More button
   */
  attachLoadMoreHandler() {
    const loadMoreBtn = document.getElementById("loadMoreBtn");
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", () => {
        this.loadMoreTransactions();
      });
    }
  }

  /**
   * Load more transactions (pagination)
   */
  loadMoreTransactions() {
    const loadMoreBtn = document.getElementById("loadMoreBtn");
    if (loadMoreBtn) {
      loadMoreBtn.disabled = true;
      loadMoreBtn.innerHTML = "⏳ ກຳລັງໂຫຼດ...";
    }

    // Load next page
    this.dashboard.loadMore();

    // Re-render transaction list
    const transactionContainer = document.getElementById(
      "transactionListContainer",
    );
    if (transactionContainer) {
      transactionContainer.innerHTML = this.dashboard.renderTransactionList();
    }

    // Re-attach load more handler
    this.attachLoadMoreHandler();

    // Re-attach delete handlers (for the list only)
    this.dashboard.attachTransactionListHandlers();
  }

  // ============================================================
  //  PROFESSIONAL PRINT REPORT GENERATOR
  // ============================================================

  /**
   * Build the period label from current filters
   */
  _getPeriodLabel() {
    const f = this.currentFilters;
    const parts = [];

    if (f.month) {
      const monthName = CONFIG.LAO_MONTHS[parseInt(f.month) - 1] || f.month;
      parts.push(`ເດືອນ ${monthName}`);
    }
    if (f.year) {
      parts.push(`ປີ ${gregorianToBuddhist(parseInt(f.year))}`);
    }
    if (f.project) parts.push(`ໂຄງການ: ${f.project}`);
    if (f.accountType) parts.push(`ບັນຊີ: ${f.accountType}`);

    return parts.length > 0 ? parts.join(" | ") : "ທຸກໄລຍະເວລາ";
  }

  /**
   * Open a new window with a clean, management-ready report and trigger print
   */
  generatePrintReport() {
    const data = this.dashboard.data;
    if (!data || !data.transactions || data.transactions.length === 0) {
      alert("ບໍ່ມີຂໍ້ມູນສຳລັບພິມລາຍງານ. ກະລຸນາໂຫຼດຂໍ້ມູນກ່ອນ.");
      return;
    }

    const transactions = data.transactions;
    const summary = data.summary || calculateSummary(transactions);
    const periodLabel = this._getPeriodLabel();
    const now = new Date();
    const printDate = formatDateShort(now);
    const printTime = now.toLocaleTimeString("lo-LA", {
      hour: "2-digit",
      minute: "2-digit",
    });

    // Categorize transactions
    const incomeTransactions = transactions.filter(
      (t) => t.type === "ລາຍຮັບ" || t.type === "income",
    );
    const expenseTransactions = transactions.filter(
      (t) => t.type === "ລາຍຈ່າຍ" || t.type === "expense",
    );

    const incomeCategories = Object.values(
      groupByCategory(incomeTransactions),
    ).sort((a, b) => b.total - a.total);

    const expenseCategories = Object.values(
      groupByCategory(expenseTransactions),
    ).sort((a, b) => b.total - a.total);

    const projects = Object.values(groupByProject(transactions)).sort(
      (a, b) => b.income + b.expense - (a.income + a.expense),
    );

    const sortedTx = sortByDate(transactions, "desc");

    // Build table rows
    const incomeCatRows = incomeCategories
      .map((c, i) => {
        const pct =
          summary.totalIncome > 0
            ? ((c.total / summary.totalIncome) * 100).toFixed(1)
            : "0.0";
        return `<tr>
        <td class="num">${i + 1}</td>
        <td>${c.category}</td>
        <td class="num">${c.count}</td>
        <td class="money">${formatCurrency(c.total)}</td>
        <td class="num">${pct}%</td>
      </tr>`;
      })
      .join("");

    const expenseCatRows = expenseCategories
      .map((c, i) => {
        const pct =
          summary.totalExpense > 0
            ? ((c.total / summary.totalExpense) * 100).toFixed(1)
            : "0.0";
        return `<tr>
        <td class="num">${i + 1}</td>
        <td>${c.category}</td>
        <td class="num">${c.count}</td>
        <td class="money">${formatCurrency(c.total)}</td>
        <td class="num">${pct}%</td>
      </tr>`;
      })
      .join("");

    const projectRows = projects
      .map((p, i) => {
        const balanceClass = p.balance >= 0 ? "positive" : "negative";
        return `<tr>
        <td class="num">${i + 1}</td>
        <td>${p.project}</td>
        <td class="money income-color">${formatCurrency(p.income)}</td>
        <td class="money expense-color">${formatCurrency(p.expense)}</td>
        <td class="money ${balanceClass}">${formatCurrency(p.balance)}</td>
      </tr>`;
      })
      .join("");

    const totalProjectIncome = projects.reduce((s, p) => s + p.income, 0);
    const totalProjectExpense = projects.reduce((s, p) => s + p.expense, 0);

    const txRows = sortedTx
      .map((t, i) => {
        const isIncome = t.type === "ລາຍຮັບ" || t.type === "income";
        const sign = isIncome ? "+" : "-";
        const cls = isIncome ? "income-color" : "expense-color";
        const currency = t.currency || "₭";
        const exchangeRate = parseFloat(t.exchangeRate) || 1;
        const isForeign = currency !== "₭";
        const rawAmount = Math.abs(parseFloat(t.amount) || 0);
        // Exchange rate cell: show "1 XXX = ₭ rate" only for foreign currencies
        const rateCell = isForeign
          ? `1 ${currency} = ${formatCurrency(exchangeRate, "₭")}`
          : "-";
        // Amount cell: show original + Kip equivalent for foreign currencies
        const amountCell = isForeign
          ? `${sign}${formatCurrency(rawAmount, currency)}<br><span style="color:#64748b;font-size:8px;">≈ ${formatCurrency(rawAmount * exchangeRate, "₭")}</span>`
          : `${sign}${formatCurrency(rawAmount, currency)}`;
        return `<tr>
        <td class="num">${i + 1}</td>
        <td>${formatDateShort(t.date)}</td>
        <td>${t.type}</td>
        <td>${t.category || "-"}</td>
        <td>${t.item || "-"}</td>
        <td>${t.project || "-"}</td>
        <td>${t.accountType || "-"}</td>
        <td class="num" style="color:#b45309;white-space:nowrap;">${rateCell}</td>
        <td class="money ${cls}">${amountCell}</td>
      </tr>`;
      })
      .join("");

    const emptyCellHtml =
      '<tr><td colspan="5" style="text-align:center;color:#94a3b8;padding:20px;">ບໍ່ມີຂໍ້ມູນ</td></tr>';

    // ---- FULL HTML TEMPLATE ----
    const html = `<!DOCTYPE html>
<html lang="lo">
<head>
  <meta charset="UTF-8">
  <title>ລາຍງານການເງິນ - ${CONFIG.COMPANY_NAME}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+Lao:wght@400;600;700;800&family=Outfit:wght@400;600;700;800&display=swap');

    @page {
      size: A4 landscape;
      margin: 0; /* Default compact margin for Page 1 & 2 */
    }
    @page detailed-list {
      margin-top: 20px; /* Reduced top margin for continuation pages as requested */
    }

    * { margin: 0; padding: 0; box-sizing: border-box; }

    body {
      font-family: 'Noto Sans Lao', 'Outfit', sans-serif;
      color: #1e293b;
      font-size: 11px;
      line-height: 1.5;
      background: #fff;
      padding: 10mm 12mm; /* Compact margins */
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    /* === HEADER / LETTERHEAD === */
    .report-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      border-bottom: 2px solid #059669;
      padding-bottom: 8px;
      margin-bottom: 10px;
    }
    .report-header .brand {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .report-header .brand img {
      height: 40px;
      width: auto;
    }
    .report-header .brand-text h1 {
      font-size: 13px;
      font-weight: 800;
      color: #059669;
      margin-bottom: 1px;
    }
    .report-header .brand-text p {
      font-size: 10px;
      color: #64748b;
    }
    .report-header .report-meta {
      text-align: right;
      font-size: 10px;
      color: #475569;
    }
    .report-header .report-meta h2 {
      font-size: 14px;
      font-weight: 800;
      color: #1e293b;
      margin-bottom: 4px;
    }

    /* === SUMMARY CARDS === */
    .summary-row {
      display: flex;
      gap: 10px;
      margin-bottom: 10px;
    }
    .s-card {
      flex: 1;
      border: 1.5px solid #e2e8f0;
      border-radius: 8px;
      padding: 8px 14px;
      position: relative;
      overflow: hidden;
    }
    .s-card::before {
      content: '';
      position: absolute;
      top: 0; left: 0;
      width: 5px;
      height: 100%;
    }
    .s-card.income::before  { background: #10b981; }
    .s-card.expense::before { background: #ef4444; }
    .s-card.balance::before { background: #3b82f6; }
    .s-card .label {
      font-size: 9px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .s-card .value {
      font-size: 16px;
      font-weight: 900;
      margin-top: 2px;
      font-variant-numeric: tabular-nums;
    }
    .s-card.income .value  { color: #059669; }
    .s-card.expense .value { color: #dc2626; }
    .s-card.balance .value { color: #2563eb; }

    /* === SECTION TITLES === */
    .section-title {
      font-size: 11px;
      font-weight: 800;
      color: #0f172a;
      margin: 8px 0 5px 0;
      padding-bottom: 4px;
      border-bottom: 1.5px solid #e2e8f0;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    /* === TABLES === */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 8px;
      font-size: 9px;
    }
    table thead th {
      background: #f1f5f9;
      color: #334155;
      font-weight: 700;
      text-align: left;
      padding: 4px 6px;
      border-bottom: 1.5px solid #cbd5e1;
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      white-space: nowrap;
    }
    table tbody td {
      padding: 3px 6px;
      border-bottom: 1px solid #f1f5f9;
      vertical-align: top;
    }
    table tbody tr:nth-child(even) { background: #fafbfc; }

    table tfoot {
      page-break-inside: avoid;
      break-inside: avoid;
    }
    table tfoot td {
      padding: 4px 6px;
      font-weight: 800;
      border-top: 1.5px solid #cbd5e1;
      background: #f8fafc;
      font-size: 9px;
    }
    /* Keep the last few rows + footer together — avoids orphaned footer on small datasets */
    table tbody tr:last-child,
    table tbody tr:nth-last-child(2),
    table tbody tr:nth-last-child(3) {
      page-break-inside: avoid;
      break-inside: avoid;
    }

    .num   { text-align: center; }
    .money { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; font-weight: 600; }

    .income-color  { color: #059669; }
    .expense-color { color: #dc2626; }
    .positive { color: #059669; font-weight: 800; }
    .negative { color: #dc2626; font-weight: 800; }

    /* Two-column layout for category tables */
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }

    /* === FOOTER === */
    .report-footer {
      margin-top: 12px;
      padding-top: 8px;
      border-top: 1.5px solid #e2e8f0;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      font-size: 8px;
      color: #94a3b8;
    }
    .signature-line {
      width: 180px;
      border-top: 1px solid #475569;
      margin-top: 30px;
      padding-top: 4px;
      text-align: center;
      font-size: 9px;
      color: #334155;
      font-weight: 600;
    }

    /* Page breaks */
    .page-break { page-break-before: always; padding-top: 4mm; }
  </style>
</head>
<body>

  <!-- ══════════ PAGE 1: SUMMARY + CATEGORY + PROJECT BREAKDOWN ══════════ -->
  <div class="report-header">
    <div class="brand">
      <img src="https://tpserver.vientianetraffic.com/ImageUpload/logowite.png" alt="Logo" onerror="this.style.display='none'">
      <div class="brand-text">
        <h1>${CONFIG.COMPANY_NAME}</h1>
        <p>ລະບົບບັນຊີລາຍຮັບລາຍຈ່າຍ</p>
      </div>
    </div>
    <div class="report-meta">
      <h2>📊 ລາຍງານການເງິນ</h2>
      <p><strong>ໄລຍະ:</strong> ${periodLabel}</p>
      <p><strong>ວັນທີພິມ:</strong> ${printDate} ເວລາ ${printTime}</p>
      <p><strong>ລວມ:</strong> ${transactions.length} ລາຍການ</p>
    </div>
  </div>

  <!-- Summary Cards -->
  <div class="summary-row">
    <div class="s-card income">
      <div class="label">ລາຍຮັບລວມ</div>
      <div class="value">${formatCurrency(summary.totalIncome)}</div>
    </div>
    <div class="s-card expense">
      <div class="label">ລາຍຈ່າຍລວມ</div>
      <div class="value">${formatCurrency(summary.totalExpense)}</div>
    </div>
    <div class="s-card balance">
      <div class="label">ຍອດຄົງເຫຼືອ</div>
      <div class="value">${formatCurrency(summary.netBalance)}</div>
    </div>
  </div>

  <!-- Category Breakdown (side by side) -->
  <div class="two-col">
    <div>
      <div class="section-title">💰 ລາຍຮັບຕາມໝວດໝູ່ (${incomeCategories.length} ໝວດ)</div>
      <table>
        <thead><tr><th class="num">#</th><th>ໝວດໝູ່</th><th class="num">ຈຳນວນ</th><th class="money">ຍອດລວມ</th><th class="num">ສັດສ່ວນ</th></tr></thead>
        <tbody>${incomeCatRows || emptyCellHtml}</tbody>
        <tfoot><tr><td></td><td><strong>ລວມທັງໝົດ</strong></td><td class="num">${incomeTransactions.length}</td><td class="money">${formatCurrency(summary.totalIncome)}</td><td class="num">100%</td></tr></tfoot>
      </table>
    </div>
    <div>
      <div class="section-title">📉 ລາຍຈ່າຍຕາມໝວດໝູ່ (${expenseCategories.length} ໝວດ)</div>
      <table>
        <thead><tr><th class="num">#</th><th>ໝວດໝູ່</th><th class="num">ຈຳນວນ</th><th class="money">ຍອດລວມ</th><th class="num">ສັດສ່ວນ</th></tr></thead>
        <tbody>${expenseCatRows || emptyCellHtml}</tbody>
        <tfoot><tr><td></td><td><strong>ລວມທັງໝົດ</strong></td><td class="num">${expenseTransactions.length}</td><td class="money">${formatCurrency(summary.totalExpense)}</td><td class="num">100%</td></tr></tfoot>
      </table>
    </div>
  </div>

  <!-- ══════════ PAGE 2: PROJECT BREAKDOWN ══════════ -->
  <div class="page-break"></div>

  <div class="report-header">
    <div class="brand">
      <img src="https://tpserver.vientianetraffic.com/ImageUpload/logowite.png" alt="Logo" onerror="this.style.display='none'">
      <div class="brand-text">
        <h1>${CONFIG.COMPANY_NAME}</h1>
        <p>ລະບົບບັນຊີລາຍຮັບລາຍຈ່າຍ</p>
      </div>
    </div>
    <div class="report-meta">
      <h2>🏗️ ລາຍງານຕາມໂຄງການ</h2>
      <p><strong>ໄລຍະ:</strong> ${periodLabel}</p>
      <p><strong>ວັນທີພິມ:</strong> ${printDate}</p>
    </div>
  </div>

  <!-- Project Breakdown -->
  <div class="section-title">🏗️ ລາຍງານຕາມໂຄງການ (${projects.length} ໂຄງການ)</div>
  <table>
    <thead><tr><th class="num">#</th><th>ໂຄງການ</th><th class="money">ລາຍຮັບ</th><th class="money">ລາຍຈ່າຍ</th><th class="money">ຍອດເຫຼືອ</th></tr></thead>
    <tbody>${projectRows || emptyCellHtml}</tbody>
    <tfoot><tr><td></td><td><strong>ລວມທັງໝົດ</strong></td><td class="money income-color">${formatCurrency(totalProjectIncome)}</td><td class="money expense-color">${formatCurrency(totalProjectExpense)}</td><td class="money ${summary.netBalance >= 0 ? "positive" : "negative"}">${formatCurrency(summary.netBalance)}</td></tr></tfoot>
  </table>

  <!-- ══════════ PAGE 3: TRANSACTION DETAIL ══════════ -->
  <!-- break-before: page forces new page; page-break-inside: avoid keeps block together;
       margin-top adds spacing from top of page -->
  <div style="break-before: page; page: detailed-list; margin-top: 2mm;">

  <div class="report-header">
    <div class="brand">
      <img src="https://tpserver.vientianetraffic.com/ImageUpload/logowite.png" alt="Logo" onerror="this.style.display='none'">
      <div class="brand-text">
        <h1>${CONFIG.COMPANY_NAME}</h1>
        <p>ລະບົບບັນຊີລາຍຮັບລາຍຈ່າຍ</p>
      </div>
    </div>
    <div class="report-meta">
      <h2>📑 ລາຍການລະອຽດ</h2>
      <p><strong>ໄລຍະ:</strong> ${periodLabel}</p>
      <p><strong>ວັນທີພິມ:</strong> ${printDate}</p>
    </div>
  </div>

  <div class="section-title">📋 ລາຍການທັງໝົດ (${sortedTx.length} ລາຍການ)</div>
  <table>
    <thead>
      <tr>
        <th class="num">#</th>
        <th>ວັນທີ</th>
        <th>ປະເພດ</th>
        <th>ໝວດໝູ່</th>
        <th>ລາຍການ</th>
        <th>ໂຄງການ</th>
        <th>ບັນຊີ</th>
        <th class="num" style="color:#b45309;">ອັດຕາແລກປ່ຽນ</th>
        <th class="money">ຈຳນວນເງິນ</th>
      </tr>
    </thead>
    <tbody>${txRows}</tbody>
  </table>

  <!-- Net Balance shown only at the end of the list -->
  <div style="margin-top: 8px; border-top: 1.5px solid #cbd5e1; padding-top: 8px; display: flex; justify-content: flex-end; align-items: center; background: #f8fafc; padding: 10px;">
    <strong style="margin-right: 20px;">ຍອດລວມສຸດທິ (₭)</strong>
    <span class="money ${summary.netBalance >= 0 ? "positive" : "negative"}" style="font-size: 1.1em;">${formatCurrency(summary.netBalance)}</span>
  </div>

  <!-- Footer with Signature Lines — inside the same block so it stays with the table -->
  <div class="report-footer">
    <div>
      <p>&copy; ${new Date().getFullYear()} ${CONFIG.COMPANY_NAME}</p>
      <p>ພິມໂດຍລະບົບ ທິບພະລັງສີ ບັກຊີ | ${printDate} ${printTime}</p>
    </div>
    <div style="display: flex; gap: 60px;">
      <div class="signature-line">ຜູ້ຈັດທຳ</div>
      <div class="signature-line">ຜູ້ອະນຸມັດ</div>
    </div>
  </div>

  </div> <!-- end of detail section wrapper with page-break-inside: avoid -->

</body>
</html>`;

    // Open a dedicated print window
    const printWindow = window.open("", "_blank", "width=1200,height=800");
    if (!printWindow) {
      alert("ກະລຸນາອະນຸຍາດ Pop-up ເພື່ອພິມລາຍງານ");
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();

    // Wait for fonts & images to load, then trigger print dialog
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 600);
    };
  }
}
